import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { firestore } from "../../utils/firebase-config";
import {
  AddressInfoDTO,
  BankAccountInfoDTO,
  ObservationsDTO,
  ScrapGeneratorDTO,
  ScrapGeneratorInfoDTO,
} from "./scrap-generators-DTO";
import { COLLECTIONS } from "../../assets/enums/firebase-colections";
import {
  callFunction,
  saveNew,
  saveNewAsync,
  saveNewWithId,
  update,
} from "../../services/persistence";
import { ContactInfoDTO } from "../contacts/contacts-DTO";
import { SuccessFeeDTO } from "./components/scrap-generator-details/components/success-fee/success-fee-DTO";
import { ScrapProspectingGeneratorsDTO } from "./scrap-generator-prospecting-DTO";
import {ConditionPayDTO } from "./components/scrap-generator-details/components/condition-pay/condition-pay-DTO";

// add new scrapGenerator
export async function addNewScrapGenerator(data: ScrapGeneratorDTO) {
  data.scrapGeneratorInfo.region = `${data.addressInfo.city} - ${data.addressInfo.state}`;
  if (data.id == undefined) {
    saveNewAsync(COLLECTIONS.SCRAP_GENERATORS, data.scrapGeneratorInfo).then(
      (docRef) => {
        data.contactInfo.type = "Principal";
        data.contactInfo.originId = docRef.id;
        saveNewWithId(COLLECTIONS.ADDRESS, data.addressInfo, docRef.id);
        saveNewWithId(
          COLLECTIONS.BANK_INFO,
          { ...data.bankAccountInfo, tenantId: docRef.id },
          docRef.id
        );
        saveNewWithId(
          COLLECTIONS.CONTACTS,
          { ...data.contactInfo, tenantId: data.contactInfo.originId },
          docRef.id + "contact"
        );
        saveNewWithId(COLLECTIONS.OBSERVATIONS, data.observations, docRef.id);
      }
    );
  } else {
    update(COLLECTIONS.SCRAP_GENERATORS, data.scrapGeneratorInfo, data.id);
    update(COLLECTIONS.ADDRESS, data.addressInfo, data.id);
    update(COLLECTIONS.BANK_INFO, data.bankAccountInfo, data.id);
    update(COLLECTIONS.CONTACTS, data.contactInfo, data.id + "contact");
    update(COLLECTIONS.OBSERVATIONS, data.observations, data.id);

    if (data.scrapGeneratorInfo.isHeadquarter) {
      callFunction(
        "updateheadquarternamegenerator",
        { id: data.id, name: data.scrapGeneratorInfo.tradingName },
        data.id
      );
    }
  }
}

// get all branchs of scrapGenerators by id
export async function getBranchsScrapGeneratorsById(
  scrapGeneratorId: string
): Promise<ScrapGeneratorInfoDTO[]> {
  const scrapGeneratorsCollectionRef = collection(
    firestore,
    COLLECTIONS.SCRAP_GENERATORS
  );
  const q = query(
    scrapGeneratorsCollectionRef,
    where("isHeadquarter", "==", false),
    where("headquarterId", "==", scrapGeneratorId)
  );

  const querySnapshot = await getDocs(q);

  const items = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    searchData: doc
      .data()
      ["legalName"].concat(
        " ",
        doc.data()["nationalRegister"],
        " ",
        doc.data()["tradingName"],
        " ",
        doc.data()["region"]
      ),
  })) as unknown as ScrapGeneratorInfoDTO[];
  return items;
}

// get all scrapGenerators
export async function getScrapGenerators(): Promise<ScrapGeneratorInfoDTO[]> {
  const collectionRef = collection(firestore, COLLECTIONS.SCRAP_GENERATORS);
  const q = query(collectionRef, orderBy("legalName"));

  const querySnapshot = await getDocs(q);
  const items = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    searchData: doc
      .data()
      ["legalName"].concat(
        " ",
        doc.data()["nationalRegister"],
        " ",
        doc.data()["tradingName"],
        " ",
        doc.data()["region"]
      ),
  })) as unknown as ScrapGeneratorInfoDTO[];
  return items;
}

// get scrapGenerator details
export const getScrapGeneratorById = async (
  id: string
): Promise<ScrapGeneratorDTO | null> => {
  const scrapGeneratorInfo = await getScrapGeneratorInfo(id);
  const contactInfo = await getScrapGeneratorContact(id);
  const addressInfo = await getScrapGeneratorAddress(id);
  const bankAccountInfo = await getScrapGeneratorBank(id);
  const observations = await getScrapGeneratorObservations(id);

  return {
    id,
    scrapGeneratorInfo,
    addressInfo,
    bankAccountInfo,
    contactInfo,
    observations,
  };
};

export async function getScrapGeneratorInfo(
  id: string
): Promise<ScrapGeneratorInfoDTO> {
  const docRef = doc(firestore, COLLECTIONS.SCRAP_GENERATORS, id);
  const docSnap = await getDoc(docRef);

  return { id: docSnap.id, ...docSnap.data() } as ScrapGeneratorInfoDTO;
}

export async function getScrapGeneratorAddress(
  id: string
): Promise<AddressInfoDTO> {
  const docRef = doc(firestore, COLLECTIONS.ADDRESS, id);
  const docSnap = await getDoc(docRef);

  return docSnap.data() as AddressInfoDTO;
}

export async function getScrapGeneratorBank(
  id: string
): Promise<BankAccountInfoDTO> {
  const docRef = doc(firestore, COLLECTIONS.BANK_INFO, id);
  const docSnap = await getDoc(docRef);

  return docSnap.data() as BankAccountInfoDTO;
}

export async function getScrapGeneratorContact(
  id: string
): Promise<ContactInfoDTO> {
  const docRef = doc(firestore, COLLECTIONS.CONTACTS, id + "contact");
  const docSnap = await getDoc(docRef);

  return docSnap.data() as ContactInfoDTO;
}

export async function getScrapGeneratorObservations(
  id: string
): Promise<ObservationsDTO> {
  const docRef = doc(firestore, COLLECTIONS.OBSERVATIONS, id);
  const docSnap = await getDoc(docRef);

  return docSnap.data() as ObservationsDTO;
}

export async function getSuccessFee(id: string): Promise<SuccessFeeDTO> {
  const docRef = doc(firestore, COLLECTIONS.FEE, id + "fee");
  const docSnap = await getDoc(docRef);

  return { id: docSnap.id, ...docSnap.data() } as SuccessFeeDTO;
}

export async function addNewSuccessFee(data: SuccessFeeDTO, id: string) {
  const numericValue = parseFloat(data.value.toString().replace(",", "."));

  if (!/^\d+(\.\d{1,2})?$/.test(numericValue.toFixed(2))) {
    throw new Error("O valor deve ter até duas casas decimais.");
  }
  data.value = parseFloat(numericValue.toFixed(2));

  if (data.id == undefined) {
    data.originId = id;
    await saveNewWithId(
      COLLECTIONS.FEE,
      { ...data, tenantId: data.originId },
      id + "fee"
    );
  } else {
    await update(COLLECTIONS.FEE, data, data.id);
  }
}

export async function getConditionPay(id: string): Promise<ConditionPayDTO> {
  const docRef = doc(firestore, COLLECTIONS.CONDITION, id + "PaymentConditions");
  const docSnap = await getDoc(docRef);

  return { id: docSnap.id, ...docSnap.data() } as ConditionPayDTO;
}

export async function addNewConditionPay(data: ConditionPayDTO, id: string) {

  if (data.id == undefined) {
    data.originId = id;
    await saveNewWithId(
      COLLECTIONS.CONDITION,
      { ...data, tenantId: data.originId },
      id + "PaymentConditions"
    );
  } else {
    await update(COLLECTIONS.CONDITION, data, data.id);
  }
}

export async function getProspectingGenerators(): Promise<
  ScrapProspectingGeneratorsDTO[]
> {
  const collectionRef = collection(
    firestore,
    COLLECTIONS.PROSPECTING_GENERATORS
  );
  const q = query(collectionRef);

  const querySnapshot = await getDocs(q);
  const items = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    searchData: doc.data()["name"].concat(" ", doc.data()["description"]),
  })) as unknown as ScrapProspectingGeneratorsDTO[];
  return items;
}

export async function getProspectingGeneratorById(
  id: string
): Promise<ScrapProspectingGeneratorsDTO> {
  const docRef = doc(firestore, COLLECTIONS.PROSPECTING_GENERATORS, id);
  const docSnap = await getDoc(docRef);
  return docSnap.data() as ScrapProspectingGeneratorsDTO;
}

export async function addOrEditProspectingGenerator(
  data: ScrapProspectingGeneratorsDTO,
  id?: string
): Promise<void> {
  const filteredSteps = data.CRMSteps?.filter(
    (step) => step.conclusionDate !== undefined && step.conclusionDate !== ""
  );

  const updatedData = {
    ...data,
    CRMSteps: filteredSteps,
  };
  if (id === undefined) {
    await saveNewAsync(COLLECTIONS.PROSPECTING_GENERATORS, updatedData);
  } else {
    await update(COLLECTIONS.PROSPECTING_GENERATORS, updatedData, id);
  }
}
